/*
 * @Author: yaoyc
 * @since: 2021-07-31 17:01:19
 * @LastAuthor: yaoyc
 * @lastTime: 2021-08-01 22:54:35
 * @FilePath: /app-client-p/src/utils/upload.ts
 * @Description:
 */
import { MessageType } from '../constants/types';
import { PickerResult } from './PicOrCameraManager';
import { isAndroid, isIos } from '../AppEvent';
import { Str_FileSizeLimit, Str_ImgUploadFail } from '../language/string_res';
import { showToast } from '../components/Toast';
import ReactBlobUtil, { UploadKey } from './ReactBlobUtil';
import { CryptEngine } from './CryptEngine';
import { Platform } from 'react-native';
import * as imSdk from '../../imsdk/api';
import RNFS from './shims/react-native-fs';

// Web 上转换 SVG 为 JPEG
async function convertSvgToJpeg(svgDataUrl: string): Promise<string | null> {
	if (Platform.OS !== 'web') {
		return null;
	}

	return new Promise((resolve) => {
		try {
			const img = document.createElement('img');
			img.onload = () => {
				const canvas = document.createElement('canvas');
				canvas.width = img.width || 512;
				canvas.height = img.height || 512;

				const ctx = canvas.getContext('2d');
				if (!ctx) {
					resolve(null);
					return;
				}

				ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
				canvas.toBlob((blob) => {
					if (blob) {
						const url = URL.createObjectURL(blob);
						resolve(url);
					} else {
						resolve(null);
					}
				}, 'image/jpeg', 0.9);
			};

			img.onerror = () => {
				resolve(null);
			};

			img.src = svgDataUrl;
		} catch (error) {
			console.warn('[upload.ts] convertSvgToJpeg 错误:', error);
			resolve(null);
		}
	});
}

export type FileTokenResponse = {
	keys: string;
	token: string;
	upload_url: string;
	download_url: string;
};
export type FileResponse = {
	url: string;
	localPath: string;
	width: number;
	height: number;
	type: MessageType;
	mimeType: string;
};
export type FileUploadOption = {
	uri: string; // 文件本地地址
	filemd5: string; // 文件hash值
	filetype: string; // 文件后缀
	contentType: string; // 文件类型
};
function md5hash(id: string) {
	return CryptEngine.md5(`${id}${Date.now().toString()}`);
}

export const uploadVoice = async (path: string, voiceId: string, key: UploadKey = UploadKey.Chat): Promise<{ code: number; msg?: string; url?: string, path?: string }> => {
	let filePath = path.startsWith('file://') ? path : ('file://' + path);
	const split = filePath.split('.');
	let filetype = 'acc';
	if (split && split.length > 0) {
		const index = split.length - 1;
		filetype = split[index];
	}

	let maxMetaSizeLimit = imSdk.getInstance().getMediaLimit(key, filetype);
	if (maxMetaSizeLimit <= 0) {
		return { code: -1, msg: "no supper file" }
	}
	const fileSize = await RNFS.stat(filePath);
	if (maxMetaSizeLimit > 0 && Number(fileSize) > maxMetaSizeLimit) {
		return { code: -1, msg: Str_FileSizeLimit }
	}


	const filemd5 = md5hash(filePath);
	const uri = isIos ? decodeURIComponent(filePath.replace('file://', '')) : filePath;
	return ReactBlobUtil.upload({ path: uri, filetype: filetype, fileName: filemd5, key: key });
};

export const uploadImages = async (pickerResults?: PickerResult[], key: UploadKey = UploadKey.Chat): Promise<{ responses: FileResponse[] }> => {
	const responses: FileResponse[] = [];
	for (const asset of pickerResults ?? []) {
		const type = asset.type;
		let mimeType = asset.mimeType;
		let msgType: MessageType
		if (type === "doc") {
			msgType = MessageType.DOCUMENT;
		} else if (type === "photo" || mimeType.includes("octet-stream")) {
			msgType = MessageType.IMAGE;
		} else {
			msgType = MessageType.VIDEO;
		}
		let width = (asset.width ?? 0) > 0 ? asset.width : 0;
		let height = (asset.height ?? 0) > 0 ? asset.height : 0;
		let filePath: string;

		// Web 上，path 是 data URL，直接使用
		filePath = asset.path;
		console.log('[upload.ts] Web 上传 - path:', filePath.substring(0, 50) + '...', 'mimeType:', mimeType);
		// 对于非图片/视频文件，直接使用，不进行压缩
		const isImageFile = mimeType.startsWith('image/');

		// 如果是 SVG 格式，需要转换为位图（Canvas 可以处理）
		if (isImageFile && mimeType === 'image/svg+xml' && key === UploadKey.Profile) {
			console.log('[upload.ts] 检测到 SVG 格式的头像，转换为 JPEG');
			try {
				const convertedPath = await convertSvgToJpeg(filePath);
				if (convertedPath) {
					filePath = convertedPath;
					mimeType = 'image/jpeg';
				}
			} catch (error) {
				console.warn('[upload.ts] SVG 转换失败，使用原文件:', error);
			}
		}

		if (isImageFile && asset.width && asset.height) {
			width = asset.width;
			height = asset.height;
		}

		// 提取文件扩展名
		let filetype: string;
		if (Platform.OS === 'web' && filePath.startsWith('data:')) {
			// Web 上，从 mimeType 提取文件类型
			// mimeType 格式: image/png, image/jpeg, video/mp4 等
			const mimeMatch = mimeType.match(/\/([^;]+)/);
			if (mimeMatch) {
				const ext = mimeMatch[1];
				// 处理常见的文件扩展名映射
				const extMap: { [key: string]: string } = {
					'jpeg': 'jpg',
					'png': 'png',
					'gif': 'gif',
					'webp': 'webp',
					'svg+xml': 'svg', // SVG 格式映射
					'vnd.openxmlformats-officedocument.wordprocessingml.document': 'docx',
					'vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'xlsx',
					'vnd.openxmlformats-officedocument.presentationml.presentation': 'pptx',
				};
				filetype = extMap[ext] || ext;
			} else {
				// 如果无法从 mimeType 提取，默认使用 jpg
				filetype = 'jpg';
			}
		} else {
			// 非 Web 平台，从文件路径提取扩展名
			const split = filePath.split('.');
			filetype = split[split.length - 1] || 'jpg';
		}

		// 对于头像上传（key === 'profile'），限制只允许常见图片格式
		if (key === UploadKey.Profile) {
			const allowedTypes = ['jpg', 'jpeg', 'png', 'gif', 'webp'];
			if (!allowedTypes.includes(filetype.toLowerCase())) {
				console.warn(`[upload.ts] 头像上传不支持 ${filetype} 格式，转换为 jpg`);
				// 如果是不支持的格式（如 SVG），转换为 jpg
				filetype = 'jpg';
			}
		}

		let maxMetaSizeLimit = imSdk.getInstance().getMediaLimit(key, filetype);
		if (maxMetaSizeLimit <= 0) {
			showToast("no supper file");
			continue;
		}

		const fileSize = await RNFS.stat(filePath);
		if (maxMetaSizeLimit > 0 && Number(fileSize) > maxMetaSizeLimit) {
			showToast(Str_FileSizeLimit);
			continue;
		}

		const filemd5 = md5hash(asset.path);
		const uri = Platform.OS === 'web' ? filePath : (isIos ? decodeURIComponent(filePath.replace('file://', '')) : filePath);
		console.log('[upload.ts] 准备上传 - filetype:', filetype, 'fileName:', filemd5, 'key:', key);
		const { code, msg, url, path } = await ReactBlobUtil.upload({ path: uri, filetype: filetype, fileName: filemd5, key: key });
		console.log('[upload.ts] 上传结果 - code:', code, 'url:', url, 'msg:', msg);
		if (code !== 0 || !url) {
			showToast(msg || Str_ImgUploadFail);
			continue;
		}
		const response: FileResponse = { url: url, width: width, height: height, type: msgType, mimeType: mimeType, localPath: path ?? "" };
		responses.push(response);
	}
	return { responses };
};
